import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';

import logo from '../../assets/img/logo-xpens.svg';
import DragAndDrop from '../../components/drag-drop';
import uploadIcon from '../../assets/img/icon-upload.svg';
import fileIcon from '../../assets/img/icon-file.svg';
import closeIcon from '../../assets/img/icon-close.svg';
import fileUploadedIcon from '../../assets/img/icon-file-uploaded.svg';
import uploadImageIcon from '../../assets/img/icon-uploading.svg';
import withRouter from '../../components/withRouter';

import { fetch } from '../../utils/api-wrapper';
import InputAutocomplete from '../../components/input-autocomplete/input-autocomplete';

// Allowed MIME types for uploads
const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
export const TRANSACTION_TYPES = ['invoice', 'expense_invoice'];

// Helper to format size (MB with 2 decimals)
const formatSizeMB = (file) => (file.size / 1000 / 1000).toFixed(2);

// Derive a short label for the file type
const shortTypeLabel = (type) => {
  if (type.includes('application/pdf')) return 'PDF';
  if (type.includes('image/png')) return 'PNG';
  if (type.includes('image/jpeg')) return 'JPG';
  return 'N/A';
};

const NewInvoice = ({ data, navigate }) => {
  const [files, setFiles] = useState([]); // Files selected (never mutated in place)
  const [errors, setErrors] = useState([]); // Validation or upload errors (final when completed)
  const [success, setSuccess] = useState([]); // Successfully uploaded file names
  const [step, setStep] = useState('init'); // init | uploading | completed
  const [currentUploadStr, setCurrentUploadStr] = useState(null); // Display string for current uploading file
  const [currentIndex, setCurrentIndex] = useState(0); // Index of file currently uploading
  const [userCards, setUserCards] = useState([]); // User's credit cards for selection
  const [defaultTransactionType, setDefaultTransactionType] = useState(TRANSACTION_TYPES[0]); // Transaction type for the selected card

  // Refs to accumulate results during upload process without triggering many rerenders
  const successRef = useRef([]);
  const errorRef = useRef([]);

  // Hide the global menu while on this page
  useEffect(() => {
    data.hideMenu = true;
    return () => {
      data.hideMenu = false;
    };
  }, [data]);

  useEffect(() => {
    fetch(
      '/c_card/',
      'GET',
      {},
      (response) => {
        setUserCards(response?.data?.c_cards || []);
      },
      null,
      false
    );
  }, []);

  useEffect(() => {
    if (userCards && userCards.length > 0) {
      setDefaultTransactionType(userCards[0]?.default_transaction_type || 'expense_invoice');
    }
  }, [userCards]);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const handleDrop = useCallback(
    (dropped) => {
      const incoming = Array.from(dropped || []);
      if (!incoming.length) return;

      const newErrors = [];
      const existingNames = new Set(files.map((f) => f.name));
      const filtered = [];

      incoming.forEach((file) => {
        if (!file?.name) return;
        if (existingNames.has(file.name)) return; // Skip duplicates silently
        if (!ALLOWED_TYPES.includes(file.type)) {
          newErrors.push(data.t.read('invalid_upload_file_type'));
          return;
        }
        filtered.push(file);
      });

      if (filtered.length) setFiles((prev) => [...prev, ...filtered]);
      if (newErrors.length) setErrors(newErrors);
    },
    [files, data]
  );

  const removeFile = useCallback((file) => {
    setFiles((prev) => prev.filter((f) => f !== file));
    setErrors([]); // Clear prior validation errors when user edits selection
  }, []);

  const resetUploadTracking = () => {
    successRef.current = [];
    errorRef.current = [];
    setSuccess([]);
    setErrors([]);
    setCurrentIndex(0);
    setCurrentUploadStr(null);
  };

  // Core upload routine for a single file at index
  const uploadFileAtIndex = useCallback(
    (index) => {
      const file = files[index];
      if (!file) return; // Safety check

      const sizeStr = `${formatSizeMB(file)} ${data.t.read('mb_unit')}`;
      setCurrentUploadStr(`${file.name} (${sizeStr})`);

      const formData = new FormData();
      // Backend expects pdf OR image key
      if (file.type.includes('application/pdf')) {
        formData.append('pdf', file);
      } else {
        formData.append('image', file);
      }

      formData.append('type', 'p-card');
      formData.append('transaction_type', file.transactionType || defaultTransactionType);

      fetch(
        `/invoices`,
        'POST',
        formData,
        (resp) => {
          successRef.current.push(file.name);
          proceedToNext(index);
        },
        (response) => {
          let msg = file.name;
          if (response?.response?.data?.message) {
            msg += ' : ' + response.response.data.message;
          }
          // Collect error
          errorRef.current.push(msg);
          proceedToNext(index);
        },
        true
      );
    },
    [files, data, defaultTransactionType]
  );

  const proceedToNext = (indexJustFinished) => {
    const nextIndex = indexJustFinished + 1;
    if (nextIndex >= files.length) {
      // Completed all uploads
      setStep('completed');
      setErrors(errorRef.current.slice());
      setSuccess(successRef.current.slice());
      setCurrentUploadStr(null);
      return;
    }
    setCurrentIndex(nextIndex);
  };

  // When in uploading step and currentIndex changes, trigger upload of that file
  useEffect(() => {
    if (step === 'uploading') {
      uploadFileAtIndex(currentIndex);
    }
  }, [step, currentIndex, uploadFileAtIndex]);

  const submitInvoices = useCallback(() => {
    if (!files.length) return; // nothing to upload
    resetUploadTracking();
    setStep('uploading');
  }, [files]);

  const acceptAttr = ALLOWED_TYPES.join(',');

  const getTransactionTypeLabel = (type) => {
    return data.t.read(`transaction_type.${type}`);
  };

  // Decide which content to show based on current step
  let content;
  if (step === 'uploading') {
    content = (
      <div className='uploading-container'>
        <div>
          <img src={uploadImageIcon} alt='Uploading' />
        </div>
        <div className='font-weight-bold font-large'>{data.t.read('uploading')}</div>
        <div className='font-small semi-faded'>{currentUploadStr}</div>
        <div className='font-small semi-faded mt-2'>
          {currentIndex + 1} / {files.length}
        </div>
      </div>
    );
  } else if (step === 'completed') {
    content = (
      <div className='uploading-container'>
        {errors.length > 0 && (
          <div className='alert alert-warning' role='alert'>
            {errors.map((err, i) => (
              <div key={'err-' + i}>{err}</div>
            ))}
          </div>
        )}
        <div>
          <img src={fileUploadedIcon} alt='File Uploaded' />
        </div>
        <div className='font-weight-bold mt-3'>{data.t.read('upload_completed')}</div>
        <div className='font-small semi-faded mb-5'>
          {data.t.read('invoice_s') + ' '}
          {success.map((name) => (
            <span key={name} className='mr-1'>
              {name}
            </span>
          ))}
          {' ' + data.t.read('invoice_submitted')}
        </div>
        <div className='mt-3'>
          <button type='button' className='btn btn-primary btn-no-border-radius btn-primary-shadow pl-5 pr-5' onClick={goBack}>
            {data.t.read('done')}
          </button>
        </div>
      </div>
    );
  } else {
    content = (
      <DragAndDrop handleDrop={handleDrop}>
        {files.length > 0 ? (
          <div className='new-files-container'>
            <div className='mb-auto full-width'>
              {files.map((file) => (
                <div key={file.name} className='invoice-item d-flex justify-content-between mb-3'>
                  <div className='invoice-file-info'>
                    <div className='font-weight-bold'>{file.name}</div>
                    <div className='font-small semi-faded'>
                      {formatSizeMB(file)} {data.t.read('mb_unit')}
                      <span className='ml-1'>{shortTypeLabel(file.type)}</span>
                    </div>
                    <InputAutocomplete
                      options={TRANSACTION_TYPES.map((type) => ({ label: getTransactionTypeLabel(type), value: type }))}
                      onChange={(selected) => {
                        file.transactionType = selected.value;
                      }}
                      placeholder={data.t.read('transaction_type.select_transaction_type')}
                      title={data.t.read('transaction_type.title')}
                      value={
                        file.transactionType
                          ? { label: getTransactionTypeLabel(file.transactionType), value: file.transactionType }
                          : { label: getTransactionTypeLabel(defaultTransactionType), value: defaultTransactionType }
                      }
                    />
                  </div>
                  <button
                    type='button'
                    className='ml-4 pointer btn btn-link p-0'
                    aria-label={data.t.read('remove') || 'Remove'}
                    onClick={() => removeFile(file)}
                  >
                    <img src={closeIcon} alt='' aria-hidden='true' />
                  </button>
                </div>
              ))}
            </div>
            <div className='invoice-item p-0'>
              <label className='pointer p-3' style={{ width: '100%', height: '100%', display: 'block' }}>
                <img src={fileIcon} alt='New invoice' className='mr-4' />
                {data.t.read('add_another_cc_invoice')}
                <input type='file' style={{ display: 'none' }} multiple accept={acceptAttr} onChange={(evt) => handleDrop(evt.target.files)} />
              </label>
            </div>
            <div className='mt-4'>
              <button
                type='button'
                disabled={!files.length}
                className='btn btn-primary btn-no-border-radius btn-primary-shadow pl-5 pr-5'
                onClick={submitInvoices}
              >
                {data.t.read('submit')}
              </button>
            </div>
          </div>
        ) : (
          <div className='first-files-container'>
            <div>
              <img src={uploadIcon} alt='XPENS logo' />
            </div>
            <div className='font-weight-bold mt-3'>{data.t.read('upload_new_cc_invoice')}</div>
            <div className='font-small semi-faded mb-5'>{data.t.read('browse_files')}</div>
            <div>
              <label className='btn btn-primary btn-no-border-radius btn-primary-shadow pl-5 pr-5'>
                {data.t.read('browse')}
                <input type='file' style={{ display: 'none' }} multiple accept={acceptAttr} onChange={(evt) => handleDrop(evt.target.files)} />
              </label>
            </div>
          </div>
        )}
      </DragAndDrop>
    );
  }

  return (
    <div className='new-invoice'>
      <div className='pointer position-absolute text-primary pointer' style={{ left: 20, top: 20 }}>
        <img src={logo} alt='XPENS logo' />
      </div>
      <button
        type='button'
        className='position-absolute text-primary pointer btn btn-link'
        style={{ right: 20, top: 20 }}
        onClick={goBack}
        aria-label={data.t.read('back')}
      >
        {data.t.read('back')}
      </button>
      <div className='page-centered'>{content}</div>
    </div>
  );
};

NewInvoice.propTypes = {
  data: PropTypes.shape({
    hideMenu: PropTypes.bool,
    t: PropTypes.shape({
      read: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  navigate: PropTypes.func.isRequired,
};

export default inject('data')(withRouter(observer(NewInvoice)));
